import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { Subject, takeUntil, finalize } from 'rxjs';
import { EmailLogsService } from 'src/app/services/email-logs.service';
import {
  EMAIL_EVENT_TYPES,
  EmailEventType,
  IEmailEventView,
  IEmailLogsQuery,
} from 'src/app/models/email-logs/email-event';

@Component({
  selector: 'app-email-logs',
  templateUrl: './email-logs.component.html',
  styleUrls: ['./email-logs.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: false,
})
export class EmailLogsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() recipientEmail?: string;

  readonly displayedColumns: readonly string[] = [
    'timestamp',
    'eventType',
    'recipientEmail',
    'subType',
    'source',
    'configSet',
  ];
  readonly eventTypes: readonly EmailEventType[] = EMAIL_EVENT_TYPES;

  events: readonly IEmailEventView[] = [];
  total: number = 0;
  pageNumber: number = 1;
  pageSize: number = 25;
  loading: boolean = false;
  filterEmail: string = '';
  selectedTypes: EmailEventType[] = [];

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  private destroy$ = new Subject<void>();

  constructor(
    private emailLogsService: EmailLogsService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.load();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['recipientEmail'] && !changes['recipientEmail'].firstChange) {
      this.pageNumber = 1;
      this.load();
    }
  }

  get isPerUserMode(): boolean {
    return !!this.recipientEmail;
  }

  onEmailFilterChange(value: string): void {
    this.filterEmail = value;
    this.pageNumber = 1;
    this.load();
  }

  onTypesChanged(types: EmailEventType | EmailEventType[] | null): void {
    this.selectedTypes = Array.isArray(types) ? types : types ? [types] : [];
    this.pageNumber = 1;
    this.load();
  }

  onPageChange(event: PageEvent): void {
    this.pageNumber = event.pageIndex + 1;
    this.pageSize = event.pageSize;
    this.load();
  }

  isHighlightedRow(row: IEmailEventView): boolean {
    return row.eventType === 'Bounce' || row.eventType === 'Complaint' || row.eventType === 'Reject';
  }

  rowClass(row: IEmailEventView): string {
    return this.isHighlightedRow(row) ? 'email-logs__row--warn' : '';
  }

  private load(): void {
    this.loading = true;
    const query: IEmailLogsQuery = {
      pageNumber: this.pageNumber,
      pageSize: this.pageSize,
      email: this.effectiveEmailFilter(),
      types: this.selectedTypes.length > 0 ? this.selectedTypes : undefined,
    };
    this.emailLogsService
      .listEmailLogs(query)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (result) => {
          this.events = result.results;
          this.total = result.total;
          this.cdr.markForCheck();
        },
      });
  }

  private effectiveEmailFilter(): string | undefined {
    if (this.recipientEmail) return this.recipientEmail;
    return this.filterEmail.trim() || undefined;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
